import React from "react";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { getMenuForRole } from "../utils/helper";
import { Tooltip } from "react-tooltip";

export default function Sidebar({ isExpanded }) {
  const { user } = useSelector((state) => state.user);
  const menu = getMenuForRole(user);

  if (!menu || menu.length === 0) return null;

  const others = menu.slice(1);
  const outbound = others.filter((m) => m.direction === "outbound");
  const inbound = others.filter((m) => m.direction === "inbound");
  const neutral = others.filter((m) => m.direction === "others");
  const hr = others.filter((m) => m.direction === "hr");

  /* ---------- Reusable Item ---------- */
  const Item = ({ item }) => (
    <NavLink to={item.path}>
      {({ isActive }) => (
        <div
          data-tooltip-id="sidebar-tooltip"
          data-tooltip-content={item.name}
          data-tooltip-hidden={isExpanded}
          className={`
            group relative flex items-center h-12 mx-2 rounded-xl
            transition-colors duration-200 outline-none
            ${isActive ? "bg-sky-600" : "hover:bg-slate-100"}
          `}
        >
          {/* Icon */}
          <div className="flex items-center justify-center w-12 h-12 shrink-0">
            <item.icon
              className="w-5 h-5"
              fillColor={isActive ? "#fff" : "#2563eb"}
            />
          </div>

          {/* Text (always rendered → NO flicker) */}
          <span
            className={`
              whitespace-nowrap text-sm font-medium
              transition-all duration-300
              ${
                isExpanded
                  ? "opacity-100 ml-1"
                  : "opacity-0 ml-0 w-0 overflow-hidden"
              }
              ${isActive ? "text-white" : "text-[#034e95]"}
            `}
          >
            {item.name}
          </span>
        </div>
      )}
    </NavLink>
  );

  /* ---------- Section Label (NO unmounting) ---------- */
  const SectionLabel = ({ text }) => (
    <div
      className={`
        px-4 mt-2 mb-1 text-xs font-semibold uppercase tracking-wider
        text-gray-400 transition-all duration-300
        ${isExpanded ? "opacity-100 h-4" : "opacity-0 h-0 overflow-hidden"}
      `}
    >
      {text}
    </div>
  );

  return (
    <>
      <aside
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE / Edge
        }}
        className="h-full bg-white pt-2 pb-6 relative z-40"
      >
        <nav className="flex flex-col gap-1">
          {/* Dashboard (first item) */}
          <Item item={menu[0]} />

          {/* Outbound */}
          {outbound.length > 0 && <SectionLabel text="Outbound" />}
          {outbound.map((item) => (
            <Item key={item.path} item={item} />
          ))}

          {/* Inbound */}
          {inbound.length > 0 && <SectionLabel text="Inbound" />}
          {inbound.map((item) => (
            <Item key={item.path} item={item} />
          ))}

          {/* Other */}
          {neutral.length > 0 && <SectionLabel text="Other" />}
          {neutral.map((item) => (
            <Item key={item.path} item={item} />
          ))}
          {/* Hr */}
          {hr.length > 0 && <SectionLabel text="Hr" />}
          {hr.map((item) => (
            <Item key={item.path} item={item} />
          ))}
        </nav>
        <div className="text-xs text-gray-400 text-center mt-4  bottom-2 w-full">
          {process.env.REACT_APP_VERSION}
        </div>
      </aside>

      {/* Global Tooltip for Sidebar Icons */}
      <Tooltip 
        id="sidebar-tooltip" 
        place="right" 
        offset={10}
        style={{ 
          backgroundColor: '#1f2937', 
          color: 'white', 
          fontWeight: 500, 
          fontSize: '12px',
          padding: '4px 10px',
          borderRadius: '6px',
          zIndex: 9999
        }} 
      />
    </>
  );
}
